/**
 * Furnace build-state derivation — pure-function module.
 *
 * Bridge-3 UX layer: when a Foundry build runs on a Furnace pod (the
 * cohort + flag-on rollout path), the workspace chrome surfaces a
 * "Building on Furnace" indicator and the chat surfaces actionable
 * error copy. Both surfaces consume a derived state computed here.
 *
 * Inputs (everything observable from the renderer):
 *   - `FurnaceStatus` snapshot from `app:get-furnace-status` — whether
 *     the flag is on, whether the user is in cohort, identity.
 *   - `isAiTyping` from chat-state — whether the agent loop is in
 *     flight RIGHT NOW.
 *   - VM connection state from `vm:connection-state` — whether the
 *     pod is reachable / heart-beating.
 *
 * Outputs:
 *   - {@link FurnaceBuildPhase} — coarse build phase a Furnace build
 *     can be in. Drives the indicator label + the "should the chrome
 *     surface anything at all" decision.
 *   - {@link computeFurnaceBuildState} — pure function from the
 *     inputs above to the derived state. No I/O. No subscriptions.
 *     Renderer tests prime it directly.
 *
 * Flag-OFF safety: when `furnaceStatus.cloudVmV1Enabled === false`
 * OR `furnaceStatus.cohort !== 'in'`, the derived `phase` is always
 * `'inactive'`. The chrome indicator + the error-mapping layer both
 * read `phase === 'inactive'` as "this is a local build, surface
 * nothing extra". The renderer behaviour is identical to pre-Bridge-3
 * main in that case.
 *
 * British spelling throughout.
 */

import type { FurnaceStatus } from '@volley/crucible-ipc-types/ipc-channels';
import type { VmConnectionState } from '@volley/crucible-ipc-types/vm-connection-state';

/**
 * Coarse build phase a Furnace build can be in, derived from chat +
 * VM-connection signals. Mirrors the rollout doc's user-facing copy
 * (`docs/onboarding-furnace.md`).
 *
 *   - `'inactive'` — out-of-cohort, flag-off, or no agent turn in
 *     flight. The chrome must surface nothing extra; behaviour is
 *     identical to pre-Bridge-3 main.
 *   - `'queued'` — agent turn is in flight but the VM-connection
 *     state is still `'idle'` / `'connecting'` (pod has not started
 *     streaming yet).
 *   - `'building'` — agent turn is in flight AND the VM connection
 *     is `'connected'`. The pod is actively running the agent loop.
 *   - `'reconnecting'` — agent turn is in flight AND the VM
 *     connection has fallen back to `'reconnecting'`. The chrome
 *     surfaces this so the engineer sees WHY their build paused.
 *   - `'failed'` — the agent turn ended with an error chunk whose
 *     content matched one of the known Furnace failure classes (see
 *     `furnace-error-mapping.ts`). The chrome stays surfaced until
 *     the next turn is dispatched.
 */
export type FurnaceBuildPhase = 'inactive' | 'queued' | 'building' | 'reconnecting' | 'failed';

/**
 * Derived Furnace build state. The Workspace chrome reads `.phase`
 * to decide whether to mount the `<FurnaceBuildIndicator>` at all
 * and what label / tone to render. The error-mapping layer reads
 * `.phase === 'failed'` to render the actionable recovery copy.
 */
export interface FurnaceBuildState {
  /** Coarse build phase — see {@link FurnaceBuildPhase}. */
  phase: FurnaceBuildPhase;
  /** Whether the input gates (flag + cohort) cleared. False when the
   *  build is local-only. Renderer can short-circuit on this without
   *  re-checking the FurnaceStatus shape. */
  routedToFurnace: boolean;
  /** Visible label for the chrome indicator. Empty string when
   *  `phase === 'inactive'`. Pinned by the indicator's snapshot
   *  tests so a copy change is intentional. */
  label: string;
}

/** Inputs to {@link computeFurnaceBuildState}. Every field is
 *  observable from the renderer without new IPC surfaces. */
export interface FurnaceBuildStateInput {
  /** Snapshot from `app:get-furnace-status`. `null` while the query
   *  is still loading (or when the IPC itself errored — the panel
   *  degrades to "Status unknown" in that case; the chrome
   *  indicator stays hidden). */
  furnaceStatus: FurnaceStatus | null;
  /** True iff an agent turn is currently in flight. Read from the
   *  chat reducer's `isAiTyping` field. */
  isAiTyping: boolean;
  /** Latest VM connection state from `vm:connection-state`. `null`
   *  before the first push lands (Foundry just opened the workspace
   *  on an in-cohort game; the producer hasn't dispatched yet). */
  vmConnectionState: VmConnectionState | null;
  /** True iff the most-recent terminal chunk for the current turn
   *  was classified as a Furnace failure (see
   *  `furnace-error-mapping.ts`). When set, the chrome shows the
   *  `'failed'` phase until the next turn starts. The caller resets
   *  this when a new turn is dispatched. */
  lastFurnaceFailure: boolean;
  /** True for imported/user-owned local projects, false for managed
   *  Foundry projects, null while `game:status` is still loading.
   *  Omitted preserves the pre-locality contract for existing direct
   *  callers. */
  gameImported?: boolean | null;
}

/**
 * Compute the derived Furnace build state. Pure function — no I/O,
 * no React, no subscriptions.
 *
 * The rule set, in priority order:
 *
 *   1. Routing gate. If the flag is OFF or the cohort is not `'in'`,
 *      this build runs locally — return `phase: 'inactive'`,
 *      `routedToFurnace: false`. Behaviour matches pre-Bridge-3 main.
 *   2. ACTIVE-TURN wins over a stale latch. If a new turn is in
 *      flight (`isAiTyping === true`), the previous turn's failure
 *      latch is implicitly stale — surface the active phase
 *      (`queued` / `building` / `reconnecting`) NOT `failed`. The
 *      Workspace caller also clears the latch on the
 *      `isAiTyping` flip via a passive effect; this priority rule
 *      is the synchronous defence so a render between the flip and
 *      the effect can't flash a stale `failed` indicator over an
 *      active turn (Codex round-1 MEDIUM on Bridge-3).
 *   3. Failure latch. If no turn is in flight AND
 *      `lastFurnaceFailure` is set, return `phase: 'failed'`. The
 *      chrome stays surfaced until the next turn starts (rule 2
 *      then takes over).
 *   4. No turn in flight, no latch → no chrome.
 *   5. Reconnecting. If the VM connection is `'reconnecting'`, the
 *      pod paused mid-turn — surface that explicitly so the
 *      engineer knows WHY their build paused.
 *   6. Building. If the VM connection is `'connected'`, the pod is
 *      streaming — return `phase: 'building'`.
 *   7. Default — turn in flight but the pod is not yet `'connected'`
 *      (still `'idle'` / `'connecting'` / `'unreachable'` / null) —
 *      return `phase: 'queued'`. The engineer sees the chrome turn
 *      on at dispatch time, then resolve to `'building'` as the
 *      pod warms up.
 *
 * No infrastructure URL is ever embedded in any returned label.
 */
export function computeFurnaceBuildState(input: FurnaceBuildStateInput): FurnaceBuildState {
  const { furnaceStatus, isAiTyping, vmConnectionState, lastFurnaceFailure, gameImported } = input;

  // 1. Routing gate. Flag OFF, out-of-cohort, unknown locality, or imported
  // game -> local build. `furnaceStatus === null` covers the "IPC still
  // loading or errored" case; `gameImported === null` covers the matching
  // `game:status` loading window.
  const routedToFurnace =
    gameImported !== null &&
    gameImported !== true &&
    furnaceStatus !== null &&
    furnaceStatus.cloudVmV1Enabled === true &&
    furnaceStatus.cohort === 'in';
  if (!routedToFurnace) {
    return { phase: 'inactive', routedToFurnace: false, label: '' };
  }

  // 2. ACTIVE turn wins over stale latch. When a new turn fires
  // before the Workspace's `isAiTyping` effect clears the latch,
  // this rule prevents the failed indicator from flashing over
  // the in-flight turn (Codex round-1 MEDIUM).
  if (isAiTyping) {
    if (vmConnectionState === 'reconnecting') {
      return {
        phase: 'reconnecting',
        routedToFurnace: true,
        label: 'Reconnecting to Furnace…',
      };
    }
    if (vmConnectionState === 'connected') {
      return {
        phase: 'building',
        routedToFurnace: true,
        label: 'Building on Furnace',
      };
    }
    return {
      phase: 'queued',
      routedToFurnace: true,
      label: 'Queuing Furnace build…',
    };
  }

  // 3. No turn in flight + latch set → render the failure surface.
  if (lastFurnaceFailure) {
    return {
      phase: 'failed',
      routedToFurnace: true,
      label: 'Furnace build failed',
    };
  }

  // 4. No turn in flight, no latch → no chrome.
  return { phase: 'inactive', routedToFurnace: true, label: '' };
}
